import { useState, useRef, useEffect } from "react";
import { router } from "@inertiajs/react";
import { SelectOption } from "./Select";
import useDelayedLoading from "@/hooks/use-delayed-loading";

const DEBOUNCE_DELAY = 300;

interface SearchInputProps {
  label?: string;
  placeholder?: string;
  reloadPropName: string;
  queryParam?: string;
  errors?: string;
  minChars?: number;
  onSelect: (option: SelectOption) => void;
  selectedValues?: string[];
  inline?: boolean;
  autoFocus?: boolean;
  disabled?: boolean;
}

interface SettledSearch {
  query: string;
  results: SelectOption[];
}

const SearchInput: React.FC<SearchInputProps> = ({
  label,
  placeholder = "Search...",
  reloadPropName,
  queryParam,
  errors,
  minChars = 2,
  onSelect,
  selectedValues = [],
  inline = false,
  autoFocus = false,
  disabled = false,
}) => {
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [settled, setSettled] = useState<SettledSearch | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const queryRef = useRef(query);
  const { loading, start, stop } = useDelayedLoading();
  const paramName = queryParam || `${reloadPropName}_q`;

  const answeredResults =
    settled && settled.query === query ? settled.results : null;
  const filteredResults = (answeredResults ?? []).filter(
    (r) => !selectedValues.includes(r.value),
  );

  useEffect(() => {
    queryRef.current = query;
  }, [query]);

  useEffect(() => {
    if (query.length >= minChars) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        router.reload({
          only: [reloadPropName],
          data: { [paramName]: query },
          onStart: start,
          onFinish: stop,
          onSuccess: (page) => {
            if (queryRef.current !== query) return;

            setSettled({
              query,
              results:
                (page.props[reloadPropName] as SelectOption[] | undefined) ??
                [],
            });
          },
        });
      }, DEBOUNCE_DELAY);
    }
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [query, reloadPropName, paramName, minChars, start, stop]);

  useEffect(() => {
    setHighlightedIndex(0);
  }, [settled]);

  const handleSelect = (option: SelectOption) => {
    onSelect(option);
    setQuery("");
    setIsOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen || filteredResults.length === 0) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex((i) => (i + 1) % filteredResults.length);
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex(
          (i) => (i - 1 + filteredResults.length) % filteredResults.length,
        );
        break;
      case "Enter":
        e.preventDefault();
        handleSelect(filteredResults[highlightedIndex]);
        break;
      case "Escape":
        setIsOpen(false);
        inputRef.current?.blur();
        break;
    }
  };

  const hasAnswer = answeredResults !== null;
  const showDropdown =
    !disabled && isOpen && hasAnswer && filteredResults.length > 0;
  const showNoResults =
    !disabled && isOpen && hasAnswer && filteredResults.length === 0;
  const showLoading = !disabled && isOpen && !hasAnswer && loading;

  return (
    <div
      className={
        inline
          ? "relative min-w-[120px] flex-1"
          : "relative flex w-full flex-col gap-2"
      }
    >
      {!inline && label && (
        <label className="text-cream-800 dark:text-cream-400 text-sm font-medium">
          {label}
        </label>
      )}
      <input
        ref={inputRef}
        type="text"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setIsOpen(true);
        }}
        onFocus={() => setIsOpen(true)}
        onBlur={() => setTimeout(() => setIsOpen(false), 150)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        className="border-cream-500 placeholder-cream-600 selection:bg-peach-300 dark:placeholder-cream-500 text-cream-950 dark:bg-cream-800 dark:text-cream-100 disabled:bg-cream-100 disabled:text-cream-600 dark:disabled:bg-cream-900 dark:disabled:text-cream-500 w-full rounded border font-medium disabled:cursor-not-allowed"
        autoComplete="off"
        autoFocus={autoFocus}
      />
      {showDropdown && (
        <ul className="border-cream-300 dark:border-cream-700 dark:bg-cream-900 absolute top-full left-0 z-10 mt-1 max-h-60 w-full overflow-auto rounded border bg-white shadow-lg">
          {filteredResults.map((option, index) => (
            <li
              key={option.value}
              onMouseDown={() => handleSelect(option)}
              onMouseEnter={() => setHighlightedIndex(index)}
              className={`cursor-pointer px-3 py-2 ${
                index === highlightedIndex
                  ? "bg-peach-100 dark:bg-cream-800"
                  : "hover:bg-cream-50 dark:hover:bg-cream-800"
              }`}
            >
              {option.label}
            </li>
          ))}
        </ul>
      )}
      {showNoResults && (
        <div className="border-cream-300 dark:border-cream-700 dark:bg-cream-900 text-cream-600 dark:text-cream-400 absolute top-full left-0 z-10 mt-1 w-full rounded border bg-white px-3 py-2 shadow-lg">
          No results found
        </div>
      )}
      {showLoading && (
        <div className="border-cream-300 dark:border-cream-700 dark:bg-cream-900 text-cream-600 dark:text-cream-400 absolute top-full left-0 z-10 mt-1 w-full rounded border bg-white px-3 py-2 shadow-lg">
          Searching...
        </div>
      )}
      {!inline && errors && (
        <span className="text-peach-600 dark:text-peach-400 text-sm font-semibold">
          {errors}
        </span>
      )}
    </div>
  );
};

export default SearchInput;
